'use memo';

import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography/Text';
import type { PasswordPolicyRequirements, PasswordRequirementCheck } from '@/lib/api/orpc/contracts/auth-password';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
type PasswordRequirementListProps = {
  id?: string;
  requirements: PasswordRequirementCheck[];
  minLength: PasswordPolicyRequirements['minLength'];
  /** 1文字以上入力済みか。未入力時は全項目を idle（neutral）表示にする。 */
  hasInput: boolean;
  /** 全要件を満たしたか。支援技術へ完了状態を通知するために使う。 */
  allMet: boolean;
  /** blur 後など、未達を強調するフェーズか。 */
  emphasizeUnmet?: boolean;
};
export function PasswordRequirementList({
  id,
  requirements,
  minLength,
  hasInput,
  allMet,
  emphasizeUnmet = false
}: PasswordRequirementListProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.passwordField'
  });
  const labelFor = (key: PasswordRequirementCheck['key']): string => {
    switch (key) {
      case 'minLength':
        return t('minLength', {
          value: minLength
        });
      case 'uppercase':
        return t('uppercase');
      case 'lowercase':
        return t('lowercase');
      case 'number':
        return t('number');
      case 'symbol':
        return t('symbol');
    }
  };
  return <div id={id}>
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2" aria-live={emphasizeUnmet ? 'polite' : 'off'}>
        {requirements.map(req => {
        const met = hasInput && req.met;
        const unmet = hasInput && !req.met && emphasizeUnmet;
        const label = labelFor(req.key);
        return <li key={req.key} className="min-w-0">
              {/* 長文折り返し時も1行目基準で揃えるため items-start。 */}
              <div className="flex items-start gap-2">
                <Icon iconName={met ? 'CircleCheckIcon' : 'CircleIcon'} size={16} colorVariant={met ? 'success' : unmet ? 'error' : 'muted'} className="mt-0.5 shrink-0" />
                <Text variant="labelSmallReg" className={cn('min-w-0 break-words', met ? 'text-neutral-on-fill' : unmet ? 'text-error-on-neutral-fill' : 'text-neutral-muted-on-fill')}>
                  {label}
                  {hasInput && <span className="sr-only">
                      {met ? t('statusMet') : t('statusUnmet')}
                    </span>}
                </Text>
              </div>
            </li>;
      })}
      </ul>
      <span className="sr-only" aria-live="polite" role="status">
        {allMet && hasInput ? t('allMet') : ''}
      </span>
    </div>;
}